import React, { useState, useEffect, useCallback, useRef, forwardRef } from 'react';
import { B } from '../../constants';
import { borderRadius, transitions } from '../../designTokens';

const sizeMap = {
  sm: { box: 16, fs: 12, icon: 10, gap: 8 },
  md: { box: 20, fs: 14, icon: 13, gap: 10 },
  lg: { box: 24, fs: 16, icon: 16, gap: 12 },
};

const cardSizeMap = {
  sm: { py: 10, px: 14 },
  md: { py: 14, px: 18 },
  lg: { py: 18, px: 22 },
};

const buttonSizeMap = {
  sm: { py: 6, px: 14, fs: 12 },
  md: { py: 8, px: 18, fs: 14 },
  lg: { py: 12, px: 22, fs: 16 },
};

function CheckIcon({ size = 13, color = 'white' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function IndeterminateIcon({ size = 13, color = 'white' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" style={{ display: 'block' }}>
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

let idCounter = 0;

const Checkbox = forwardRef(({
  checked = false,
  onChange,
  indeterminate = false,
  disabled = false,
  error = false,
  size = 'md',
  variant = 'default',
  label,
  helperText,
  icon: customIcon,
  color = B.forest,
  showFocusRing = true,
  fullWidth = false,
  style,
  name,
  value,
  id,
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const innerInputRef = useRef(null);
  const helperIdRef = useRef(null);

  if (!helperIdRef.current) {
    helperIdRef.current = `checkbox-helper-${++idCounter}`;
  }

  useEffect(() => {
    try {
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      setReducedMotion(mq.matches);
      const handler = (e) => setReducedMotion(e.matches);
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    } catch { return undefined; }
  }, []);

  useEffect(() => {
    if (innerInputRef.current) {
      innerInputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const s = sizeMap[size] || sizeMap.md;
  const cs = cardSizeMap[size] || cardSizeMap.md;
  const bs = buttonSizeMap[size] || buttonSizeMap.md;
  const transitionValue = transitions.normal;

  const handleClick = useCallback((e) => {
    if (disabled) return;
    onChange?.({
      target: {
        name,
        value,
        checked: !checked,
        type: 'checkbox',
      },
    });
  }, [disabled, onChange, name, value, checked]);

  const handleKeyDown = useCallback((e) => {
    if (disabled) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onChange?.({
        target: {
          name,
          value,
          checked: !checked,
          type: 'checkbox',
        },
      });
    }
  }, [disabled, onChange, name, value, checked]);

  const handleFocus = useCallback(() => setIsFocused(true), []);
  const handleBlur = useCallback(() => setIsFocused(false), []);

  const setRef = useCallback((node) => {
    innerInputRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  }, [ref]);

  const isCheckedOrIndeterminate = checked || indeterminate;
  const effectiveColor = error ? B.error : disabled ? B.muted : color;
  const boxBackground = isCheckedOrIndeterminate
    ? (error ? B.error : disabled ? '#d1d5db' : color)
    : 'white';
  const boxBorderColor = error ? B.error : disabled ? '#d1d5db' : isFocused ? color : '#d1d5db';

  const focusBoxShadow = isFocused && showFocusRing && !disabled
    ? `0 0 0 3px ${effectiveColor}30`
    : 'none';

  const renderIcon = () => {
    if (customIcon && isCheckedOrIndeterminate) return customIcon;
    if (indeterminate) return <IndeterminateIcon size={s.icon} color={checked || indeterminate ? 'white' : 'transparent'} />;
    if (checked) return <CheckIcon size={s.icon} color="white" />;
    return null;
  };

  const checkboxBox = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: s.box,
        height: s.box,
        borderRadius: borderRadius.xs,
        background: boxBackground,
        border: `2px solid ${boxBorderColor}`,
        boxShadow: focusBoxShadow,
        transition: reducedMotion ? 'none' : `all ${transitionValue}`,
        flexShrink: 0,
        boxSizing: 'border-box',
      }}
    >
      {renderIcon()}
    </span>
  );

  const labelEl = label ? (
    <span
      style={{
        fontFamily: "'Montserrat',sans-serif",
        fontSize: s.fs,
        color: disabled ? B.muted : error ? B.error : B.night,
        userSelect: 'none',
        lineHeight: 1.4,
      }}
    >
      {label}
    </span>
  ) : null;

  const helperEl = helperText ? (
    <div
      id={helperIdRef.current}
      style={{
        marginTop: 6,
        fontSize: 12,
        fontFamily: "'Montserrat',sans-serif",
        color: error ? B.error : B.muted,
        lineHeight: 1.3,
      }}
    >
      {helperText}
    </div>
  ) : null;

  if (variant === 'card') {
    return (
      <div style={{ width: fullWidth ? '100%' : 'auto', ...style }}>
        <div
          role="checkbox"
          aria-checked={indeterminate ? 'mixed' : checked}
          aria-disabled={disabled || undefined}
          aria-invalid={error || undefined}
          aria-describedby={helperText ? helperIdRef.current : undefined}
          tabIndex={disabled ? -1 : 0}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: s.gap,
            width: '100%',
            padding: `${cs.py}px ${cs.px}px`,
            border: `2px solid ${error ? B.error : isFocused ? color : '#e5e0d8'}`,
            borderRadius: borderRadius.md,
            background: disabled ? '#f9f9f9' : 'white',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            transition: reducedMotion ? 'none' : `all ${transitionValue}`,
            boxShadow: focusBoxShadow,
            outline: 'none',
            boxSizing: 'border-box',
            ...props.style,
          }}
          {...props}
        >
          {checkboxBox}
          {labelEl && (
            <div style={{ flex: 1 }}>
              {labelEl}
            </div>
          )}
        </div>
        {helperEl}
      </div>
    );
  }

  if (variant === 'button') {
    return (
      <div style={{ width: fullWidth ? '100%' : 'auto', ...style }}>
        <div
          role="checkbox"
          aria-checked={indeterminate ? 'mixed' : checked}
          aria-disabled={disabled || undefined}
          aria-invalid={error || undefined}
          aria-describedby={helperText ? helperIdRef.current : undefined}
          tabIndex={disabled ? -1 : 0}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: s.gap,
            padding: `${bs.py}px ${bs.px}px`,
            borderRadius: borderRadius.pill,
            background: checked || indeterminate
              ? (error ? B.error : disabled ? '#d1d5db' : color)
              : (error ? '#fef2f2' : disabled ? '#f9f9f9' : 'white'),
            border: `2px solid ${error ? B.error : disabled ? '#d1d5db' : checked || indeterminate ? color : '#e5e0d8'}`,
            color: checked || indeterminate ? 'white' : (error ? B.error : B.night),
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            fontFamily: "'Montserrat',sans-serif",
            fontSize: bs.fs,
            fontWeight: 600,
            transition: reducedMotion ? 'none' : `all ${transitionValue}`,
            boxShadow: isFocused && showFocusRing && !disabled ? `0 0 0 3px ${effectiveColor}30` : 'none',
            outline: 'none',
            whiteSpace: 'nowrap',
            ...props.style,
          }}
          {...props}
        >
          {isCheckedOrIndeterminate && !customIcon && (indeterminate ? (
            <IndeterminateIcon size={s.icon} color={checked || indeterminate ? 'white' : 'transparent'} />
          ) : (
            <CheckIcon size={s.icon} color="white" />
          ))}
          {customIcon && isCheckedOrIndeterminate && customIcon}
          {label}
        </div>
        {helperEl}
      </div>
    );
  }

  return (
    <div style={{ width: fullWidth ? '100%' : 'auto', ...style }}>
      <div
        role="checkbox"
        aria-checked={indeterminate ? 'mixed' : checked}
        aria-disabled={disabled || undefined}
        aria-invalid={error || undefined}
        aria-describedby={helperText ? helperIdRef.current : undefined}
        tabIndex={disabled ? -1 : 0}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: s.gap,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          outline: 'none',
          ...props.style,
        }}
        {...props}
      >
        <input
          ref={setRef}
          type="checkbox"
          name={name}
          value={value}
          checked={checked}
          disabled={disabled}
          onChange={() => {}}
          style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
          aria-hidden="true"
          tabIndex={-1}
        />
        {checkboxBox}
        {labelEl}
      </div>
      {helperEl}
    </div>
  );
});

export default Checkbox;
